import { useEffect, useState } from 'react';
import { adviserApi, type ClientLinkRequest } from '../../advisers/api';
import { NOTIFICATIONS_CHANGED_EVENT } from '../../advisers/enquiriesApi';

export default function ClientAdviserAccessPanel({ companyId }: { companyId?: string } = {}) {
  const [links, setLinks] = useState<ClientLinkRequest[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<ClientLinkRequest | null>(null);
  const refresh = async () => {
    const data = await adviserApi.pendingLinks();
    setLinks(data);
  };
  useEffect(() => {
    let active = true;
    adviserApi
      .pendingLinks()
      .then((data) => {
        if (active) setLinks(data);
      })
      .catch((err) => {
        if (active) setError(err.message);
      });
    return () => {
      active = false;
    };
  }, []);
  const change = async (link: ClientLinkRequest, approve: boolean) => {
    setBusy(true);
    setError('');
    try {
      await (approve ? adviserApi.approveLink(link.id) : adviserApi.revokeLink(link.id));
      window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
      await refresh();
      setConfirm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update adviser access.');
    } finally {
      setBusy(false);
    }
  };
  const visibleLinks = companyId ? links.filter((link) => link.companyId === companyId) : links;
  if (!visibleLinks.length && !error) return null;
  return (
    <section className="adviser-panel">
      <h2>Adviser access to your companies</h2>
      <p>
        You retain ownership and direct access. Approving a practice allows its authorised advisers
        to edit your company's planning and execution content.
      </p>
      {error && (
        <p className="adviser-error" role="alert">
          {error}
        </p>
      )}
      {visibleLinks.map((link) => (
        <div className="adviser-team-row" key={link.id}>
          <div>
            <strong>{link.companyName}</strong>
            <p>
              {link.practiceName} ·{' '}
              {link.status === 'pending' ? 'Requests delegated access' : 'Approved adviser access'}
            </p>
          </div>
          {link.status === 'pending' && (
            <button
              className="adviser-button"
              type="button"
              disabled={busy}
              onClick={() => void change(link, true)}
            >
              Approve practice
            </button>
          )}
          <button
            className="adviser-button adviser-button-quiet"
            type="button"
            disabled={busy}
            onClick={() => setConfirm(link)}
          >
            {link.status === 'pending' ? 'Decline' : 'Revoke access'}
          </button>
        </div>
      ))}
      {confirm && (
        <div className="adviser-modal-backdrop">
          <section
            className="adviser-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="revoke-adviser-title"
          >
            <h2 id="revoke-adviser-title">
              {confirm.status === 'pending' ? 'Decline' : 'Revoke'} {confirm.practiceName}?
            </h2>
            <p>
              Practice members will lose delegated access immediately. Your company data and direct
              access remain. Billing commitments continue separately.
            </p>
            {error && (
              <p className="adviser-error" role="alert">
                {error}
              </p>
            )}
            <div className="adviser-actions">
              <button
                type="button"
                className="adviser-button"
                disabled={busy}
                onClick={() => void change(confirm, false)}
              >
                Confirm {confirm.status === 'pending' ? 'decline' : 'revocation'}
              </button>
              <button
                type="button"
                className="adviser-button adviser-button-secondary"
                disabled={busy}
                onClick={() => setConfirm(null)}
              >
                Cancel
              </button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
